import React, { forwardRef, useRef, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import PhoneNumber from './PhoneNumber';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import ErrorIcon from '@mui/icons-material/Error';
import Typography from '@mui/joy/Typography';


const types = {
  goodbye: {
    flexDirection: 'row',
    color: 'warning',
    maxWidth: '85%'
  },
  agent: {
    flexDirection: 'row',
    color: 'primary',
    maxWidth: '85%'
  },
  inject: {
    flexDirection: 'row',
    color: 'warning',
    maxWidth: '85%'
  },
  user: {
    flexDirection: 'row-reverse',
    color: 'neutral',
    maxWidth: '85%'
  },
  // The auxiliary ("second opinion") STT's reading of the same caller speech
  // (agent options.stt.aux), logged as `user-aux` next to the primary `user`
  // entry: caller side, its own colour and a caption, so the two recognitions
  // sit next to each other without being mistaken for one another.
  'user-aux': {
    flexDirection: 'row-reverse',
    color: 'success',
    maxWidth: '85%',
    label: 'Aux STT'
  },
  // The output audit STT's reading of the agent's OWN audio (agent
  // options.tts.output), logged as `agent-speech` next to the `agent` entry the
  // model produced: agent side, the same "independent STT" colour, its own caption.
  'agent-speech': {
    flexDirection: 'row',
    color: 'success',
    maxWidth: '85%',
    label: 'Audit STT'
  },
  call: {
    startDecorator: <CallIcon />,
    flexDirection: 'row-reverse',
    flexGrow: 1,
    align: 'center',
    color: 'success'
  },
  hangup: {
    startDecorator: <CallEndIcon />,
    flexDirection: 'row-reverse',
    flexGrow: 1,
    align: 'center',
    color: 'danger'
  },
  call_failed: {
    startDecorator: <ErrorIcon />,
    flexDirection: 'row',
    flexGrow: 1,
    color: 'danger',
    fontSize: 'small',
    align: 'center',
    whiteSpace: 'pre-wrap',
    lineHeight: '1',
    wordBreak: 'break-word'
  },
  data: {
    flexDirection: 'row-reverse',
    color: 'info'
  },
  function_results: {
    flexDirection: 'row-reverse',
    flexGrow: 1,
    color: 'warning',
    fontSize: 'small',
    align: 'center',
    whiteSpace: 'pre-wrap',
    lineHeight: '1',
    wordBreak: 'break-word'
  },
  rest_callout: {
    flexDirection: 'row',
    flexGrow: 1,
    color: 'warning',
    fontSize: 'small',
    align: 'center',
    whiteSpace: 'pre-wrap',
    lineHeight: '1',
    wordBreak: 'break-word'
  },
  error: {
    startDecorator: <ErrorIcon />,
    flexDirection: 'row',
    flexGrow: 1,
    color: 'danger',
    fontSize: 'small',
    align: 'center',
    whiteSpace: 'pre-wrap',
    lineHeight: '1',
    wordBreak: 'break-word'
  },

};

const Bubble = ({ type, index, children }) => {
  let { align, flexDirection, flexGrow, color, maxWidth, startDecorator, whiteSpace, lineHeight, wordBreak, label } = types[type] || types['agent'];

  return types[type] && <Box key={index}
    sx={{
      display: 'flex',
      flexDirection,
      bgcolor: 'background.paper',
      borderRadius: 3
    }}
  >
    <Sheet
      variant="soft"
      color={color}
      sx={{
        p: 1,
        m: 1,
        mr: 3,
        flexGrow,
        maxWidth,
        borderRadius: 10,
        lineHeight,
        wordBreak,
        whiteSpace
      }}
    >
      {label && <Typography level="body-xs" sx={{ opacity: 0.75, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</Typography>}
      <Typography {...{ startDecorator }} level="body2" justifyContent={align} align="left">{children}</Typography>

    </Sheet>

  </Box>;

};


function getActiveSegmentIndex(currentTime, segmentOffsets) {
  if (!segmentOffsets?.length || currentTime == null) return -1;
  let i = segmentOffsets.length - 1;
  while (i >= 0 && segmentOffsets[i] > currentTime) i--;
  return i;
}

const Transcript = forwardRef(function Transcript({ transcript, number, tooltip, state, audioModel, disconnect, segmentOffsets, currentTime, hideAgentStatus, disableScrollToEnd, scrollToEndWhenLastIsHangup, noBorder, onScroll }, ref) {
  const listRef = useRef(null);
  const segmentRefs = useRef([]);
  const prevTranscriptLenRef = useRef(null);
  const setWrapperRef = (el) => {
    listRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };
  const Wrapper = noBorder ? Box : Sheet;
  const wrapperSx = noBorder ? { width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto', p: 2 } : { p: 4, width: '100%', borderRadius: 5 };

  useEffect(() => {
    const len = transcript?.length ?? 0;
    const prevLen = prevTranscriptLenRef.current;
    // Prevent initial mount from auto-scrolling the whole page (especially on mobile).
    // Only auto-scroll when new transcript items are appended after first render.
    if (prevLen == null) {
      prevTranscriptLenRef.current = len;
      return;
    }
    prevTranscriptLenRef.current = len;
    if (len === 0 || len <= prevLen) return;

    if (scrollToEndWhenLastIsHangup) {
      if (transcript?.length && transcript[transcript.length - 1]?.hangup !== undefined) {
        const lastIndex = transcript.length - 1;
        const scrollToHangup = () => {
          const el = segmentRefs.current[lastIndex] ?? listRef.current?.lastElementChild;
          el?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        };
        requestAnimationFrame(() => requestAnimationFrame(scrollToHangup));
      }
    } else if (!disableScrollToEnd) {
      listRef.current?.lastElementChild?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [transcript, disableScrollToEnd, scrollToEndWhenLastIsHangup]);

  const activeIndex = segmentOffsets?.length && currentTime != null ? getActiveSegmentIndex(currentTime, segmentOffsets) : -1;

  useEffect(() => {
    if (activeIndex >= 0 && segmentRefs.current[activeIndex]) {
      segmentRefs.current[activeIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeIndex, currentTime]);

  let format_call = (value) => (`Function: ${value.name} (` + Object.entries(value.input || {}).map(([name, val], index) => (`${(index) ? ' ' : ''}${name}: ${val}`)) + `) returns ${value.result} ${value.error ? value.error : '(no error)'} \n`);

  const renderTranscriptText = (raw) => {
    if (raw == null) return '';
    let text = String(raw);
    // Strip leading "\n" sequences
    while (text.startsWith('\\n')) {
      text = text.slice(2);
    }
    // Strip trailing "\n" sequences
    while (text.endsWith('\\n')) {
      text = text.slice(0, -2);
    }
    if (!text.includes('\\n')) return text;
    const parts = text.split('\\n');
    return parts.map((part, idx) => (
      <React.Fragment key={idx}>
        {idx > 0 && <br />}
        {part}
      </React.Fragment>
    ));
  };

  return (
    <Wrapper variant={noBorder ? undefined : 'outlined'} sx={wrapperSx} ref={setWrapperRef} onScroll={onScroll}>
      {!hideAgentStatus && <PhoneNumber {...{ number, tooltip, state, audioModel }} />}
      {transcript && transcript.map((utter, index) => (
        <Box
          key={index}
          ref={(el) => { if (segmentOffsets?.length) segmentRefs.current[index] = el; }}
          sx={{
            borderRadius: 'sm',
            ...(activeIndex === index && segmentOffsets?.length ? { bgcolor: 'action.selected', transition: 'background 0.2s' } : {}),
          }}
        >
          {Object.entries(utter).map(([type, value]) => (
            <React.Fragment key={`${type}-${index}`}>
              {(type === 'function_results') ? (
                (Array.isArray(value) ? value : []).map((v, vi) => (
                  <Bubble type={type} key={`${type}-${index}-${vi}`}>
                    <Typography level="body-xs">{format_call(v)}</Typography>
                  </Bubble>
                ))
              ) : (
                <Bubble type={type} index={`${type}-${index}`}>
                  {(type === 'agent' || type === 'goodbye' || type === 'user' || type === 'user-aux' || type === 'agent-speech' || type === 'inject') && renderTranscriptText(value)}
                  {type === 'call' && `Call from ${value}`}
                  {type === 'hangup' && 'Call HANGUP'}
                  {type === 'call_failed' && `Call FAILED: ${value}`}
                  {type === 'error' && `System Error: ${value}`}
                  {(type === 'data' || type === 'function_results') && Array.isArray(value) && <Typography level="body-xs">{value.map(d => format_call(d))}</Typography>}
                  {(type === 'rest_callout') && value && <Typography level="body-xs">{`${value.method} ${value.url} ${value.body}`}</Typography>}
                </Bubble>
              )}
            </React.Fragment>
          ))}
        </Box>
      ))}
      {!!transcript && !!transcript.length && transcript[transcript.length - 1].agent &&
        <Bubble type="user" key="last">
          ...
        </Bubble>
      }

    </Wrapper>);
});

export default Transcript;
